import React, { useEffect, useMemo, useState } from 'react'

function formatISTParts(isoTs) {
  try {
    // Parse the ISO string directly without Date conversion to avoid timezone shifts
    const match = isoTs.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
    if (!match) {
      return { timeLabel: isoTs || '', dateLabel: '' }
    }

    const [, year, month, day, hour, minute] = match
    
    // Format time (already in IST from backend)
    const hr = parseInt(hour, 10)
    const min = minute
    const isPM = hr >= 12
    const hr12 = hr % 12 || 12
    const timeLabel = `${hr12}:${min} ${isPM ? 'pm' : 'am'}`

    // Get today's date for comparison
    const now = new Date()
    const todayDay = now.getDate()
    const todayMonth = now.getMonth() + 1
    const todayYear = now.getFullYear()

    const tweetDay = parseInt(day, 10)
    const tweetMonth = parseInt(month, 10)
    const tweetYear = parseInt(year, 10)

    // Check if it's today
    const isToday = todayDay === tweetDay && todayMonth === tweetMonth && todayYear === tweetYear

    if (isToday) {
      return { timeLabel, dateLabel: 'Today' }
    }

    // Format date
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const dateLabel = `${day} ${monthNames[tweetMonth - 1]}`

    return { timeLabel, dateLabel }
  } catch {
    return { timeLabel: isoTs || '', dateLabel: '' }
  }
}

function cleanTweetText(text) {
  const raw = text ?? ''
  const lines = raw.split('\n')
  const filtered = lines
    .filter((l) => {
      const t = l.trim()
      if (t.startsWith('Original Media:')) return false
      if (t.startsWith('Retweeted Media:')) return false
      if (t.startsWith('Quoted Media:')) return false
      if (t.startsWith('Poster:')) return false
      return true
    })
    .map((l) => l.replace(/^Original:\s*/, ''))
  return filtered.join('\n')
}

function normalizeSummary(summary) {
  if (!summary) return { lines: [], text: '' }

  // If already structured as an array
  if (Array.isArray(summary)) {
    const lines = summary.map((l) => String(l).trim()).filter(Boolean)
    return { lines, text: lines.join('\n') }
  }

  let raw = String(summary).trim()

  // Strip common code fences like ```json ... ```
  if (raw.startsWith('```')) {
    raw = raw.replace(/^```(?:json)?/i, '').replace(/```$/, '').trim()
  }

  // Try JSON parsing first
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      const lines = Array.isArray(parsed.report)
        ? parsed.report.map((l) => String(l).trim()).filter(Boolean)
        : Array.isArray(parsed.hindi_report)
          ? parsed.hindi_report.map((l) => String(l).trim()).filter(Boolean)
          : Array.isArray(parsed.summary_lines)
            ? parsed.summary_lines.map((l) => String(l).trim()).filter(Boolean)
            : []
      const text = lines.length ? lines.join('\n') : ''
      if (lines.length || text) return { lines, text }
    }
  } catch (e) {
    // fall through to heuristics
  }

  // Heuristic: if report/hindi_report/summary_lines appear inline, try to extract them
  const inlineMatch = raw.match(/report"\s*:\s*\[([^\]]+)\]/i) || raw.match(/hindi_report"\s*:\s*\[([^\]]+)\]/i) || raw.match(/summary_lines"\s*:\s*\[([^\]]+)\]/i)
  if (inlineMatch && inlineMatch[1]) {
    const parts = inlineMatch[1]
      .split(',')
      .map((p) => p.replace(/^"|"$/g, '').trim())
      .filter(Boolean)
    if (parts.length) return { lines: parts, text: parts.join('\n') }
  }

  // Fallback: split on newlines
  const lines = raw
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
  return { lines, text: lines.join('\n') }
}

export default function TweetCard({ item, onOpenLightbox, isNew = false, isRead = false, onMarkRead = () => {}, id }) {
  const {
    author_name,
    author_username,
    author_twitter_url,
    author_profile_image_url,
    tweet_url,
    media_urls,
    location,
    one_line_summary,
    summary,
    tweet,
    timestamp,
    importance_score
  } = item

  const clampedImportance = useMemo(() => {
    const n = Number(importance_score)
    if (!Number.isFinite(n)) return null
    return Math.max(0, Math.min(10, n))
  }, [importance_score])
  const importancePct = useMemo(() => {
    if (clampedImportance === null) return null
    return (clampedImportance / 10) * 100
  }, [clampedImportance])

  const importanceColor = useMemo(() => {
    if (clampedImportance === null) return '#27ae60'
    if (clampedImportance <= 3) return '#e74c3c' // red
    if (clampedImportance <= 5) return '#e67e22' // orange
    if (clampedImportance <= 7) return '#196f3d' // dark green
    return '#27ae60' // current green
  }, [clampedImportance])

  const headlineText = one_line_summary || item.one_line_headline || ''

  const [expanded, setExpanded] = useState(false)

  const cleanedTweet = useMemo(() => cleanTweetText(tweet), [tweet])

  const { timeLabel, dateLabel } = useMemo(() => formatISTParts(timestamp), [timestamp])

  const normalizedSummary = useMemo(() => normalizeSummary(summary), [summary])
  const summaryText = useMemo(() => {
    if (normalizedSummary.text) return normalizedSummary.text
    if (normalizedSummary.lines && normalizedSummary.lines.length) return normalizedSummary.lines.join('\n')
    return ''
  }, [normalizedSummary])

  // Don't render the card if there's no summary
  if (!summaryText) {
    return null
  }

  const shouldClamp = useMemo(() => {
    if (!cleanedTweet) return false
    return cleanedTweet.length > 180 || cleanedTweet.split('\n').length > 3
  }, [cleanedTweet])

  const handleExpand = () => {
    setExpanded(true)
    if (id) onMarkRead(id)
  }

  const handleCollapse = () => {
    setExpanded(false)
    if (id) onMarkRead(id) // remain marked as read even after collapse
  }

  const showImportanceBar = import.meta.env.VITE_SHOW_IMPORTANCE_BAR === 'true'

  return (
    <div
      className={`card${isNew ? ' card-enter' : ''}${isRead ? ' card-read' : ''}`}
      data-ts={Date.parse(timestamp) || 0}
      style={{ position: 'relative' }}
    >
      <div className="card-header">
        <div className="meta meta-row">
          <span className="meta-time">{timeLabel}</span>
          <span className="meta-date">{dateLabel}</span>
        </div>
      </div>
  <div className={`card-body ${expanded ? '' : 'card-body-compact'}`}>
        <div className="col-left">
          <div className="author-row">
            <div className="author">
              {author_profile_image_url ? (
                <img src={author_profile_image_url} alt={author_name || author_username} />
              ) : null}
              <div>
                {author_twitter_url ? (
                  <a href={author_twitter_url} target="_blank" rel="noopener noreferrer">
                    {author_name || `@${author_username}`}
                  </a>
                ) : (
                  <span>{author_name || `@${author_username}`}</span>
                )}
                {author_username ? <div style={{ fontSize: 12, color: '#a1a1aa' }}>@{author_username}</div> : null}
              </div>
            </div>
            {!expanded ? (
              <button className="btn-view-more btn-view-inline" type="button" onClick={handleExpand}>View more</button>
            ) : null}
          </div>
          {!expanded ? (
            (location || headlineText) ? (
              <div className="headline" style={{ fontWeight: 400, marginTop: 6 }}>
                {location ? <span style={{ color: '#f97316', fontWeight: 400 }}>{location}</span> : null}
                {location && headlineText ? ': ' : ''}
                {headlineText}
              </div>
            ) : null
          ) : (
            <>
              {location ? <div className="location" style={{ fontWeight: 400, color: '#f97316', marginTop: 8 }}>{location}</div> : null}
              {summaryText ? (
                <div className="summary" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{summaryText}</div>
              ) : null}
            </>
          )}
        </div>
        <div className="col-right">
          {expanded ? (
            summaryText && cleanedTweet ? <div className="divider-soft" /> : null
          ) : null}
          {expanded ? (
            <div className="tweet" style={{ whiteSpace: 'pre-wrap' }}>
              {cleanedTweet}
            </div>
          ) : null}
          {expanded ? (
            <button className="btn-view-more" type="button" onClick={handleCollapse}>View less</button>
          ) : null}
          {expanded && Array.isArray(media_urls) && media_urls.length > 0 ? (
            <div className="media">
              {media_urls
                .filter((u) => typeof u === 'string' && u.startsWith('http'))
                .map((u, i) => (
                  <img
                    key={i}
                    src={u}
                    alt="Tweet media"
                    loading="lazy"
                    onDoubleClick={() => onOpenLightbox(u)}
                    onClick={() => onOpenLightbox(u)}
                  />
                ))}
            </div>
          ) : null}
          <div className="links">{expanded && tweet_url ? <a href={tweet_url} target="_blank" rel="noopener noreferrer">View Tweet</a> : null}</div>
        </div>
      </div>
      {showImportanceBar && importancePct !== null && clampedImportance <= 4 ? (
        <div
          className="importance-bar"
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
            height: '4px',
            background: `linear-gradient(to right, ${importanceColor} ${importancePct}%, transparent ${importancePct}%)`,
            borderBottomLeftRadius: '6px',
            borderBottomRightRadius: '6px',
          }}
        />
      ) : null}
    </div>
  )
}
